// The video player inside a document body. Videos play inside the body, with
// our own player on desktop too (product decision).
//
// - Mouse devices: a control bar over the video (while the mouse moves, and
//   while paused). A click on the surface plays / pauses, a double click goes
//   full screen, keys follow `videoKeyAction`.
// - Touch devices: no playback inside the body. It shows the first frame, ▶ and
//   the length; a tap opens the full-screen viewer (video-viewer.tsx). On close
//   the inline player takes over the viewer's position.
//
// The node view and the static body **both render this as is**: two versions
// would change shape the moment the view switches after sync.
//
// The root is `contentEditable={false}` with `data-video-player`. Keeping its
// events away from ProseMirror is the node view's `stopEvent` (everything inside
// `.trameria-file-box`, which wraps this, except drag and drop).
//
// The speed menu is **drawn inside the player** (no portal): when the element
// goes full screen, anything portaled outside it is not visible.
import { m } from "@trameria/i18n";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useCoarsePointer } from "../pointer";
import {
  clampTime,
  formatVideoTime,
  stepSpeed,
  VIDEO_IDLE_MS,
  VIDEO_RETRY_MS,
  VIDEO_SPEEDS,
  type VideoKeyAction,
  videoFrameStyle,
  videoKeyAction,
} from "./video-controls";
import {
  ExitFullscreenIcon,
  FullscreenIcon,
  MuteIcon,
  PauseIcon,
  PlayIcon,
  VolumeIcon,
} from "./video-icons";
import { SeekBar } from "./video-seek-bar";
import { openVideoViewer } from "./video-viewer";

export interface VideoPlayerProps {
  /** The streaming URL. */
  src: string;
  name: string;
  /** The download URL. */
  download: string;
  /** Display width (the stored width; while resizing, the width the node view shows). */
  width: number | null;
}

export function VideoPlayer({ src, name, download, width }: VideoPlayerProps) {
  const coarse = useCoarsePointer();
  const root = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [ratio, setRatio] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [rate, setRate] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [awake, setAwake] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);

  // frontend-development: not a fetch; subscribes to full screen changes.
  useEffect(() => {
    const on = () => setFullscreen(document.fullscreenElement === root.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  // frontend-development: not a fetch; cleanup of the timers.
  useEffect(
    () => () => {
      if (idle.current) clearTimeout(idle.current);
      if (retry.current) clearTimeout(retry.current);
    },
    [],
  );
  // A press outside the speed menu closes it.
  // frontend-development: not a fetch; closes the speed menu on an outside press.
  useEffect(() => {
    if (!speedOpen) return;
    const onDown = (e: PointerEvent) => {
      if (
        !(e.target instanceof Element) ||
        !e.target.closest("[data-video-speed]")
      )
        setSpeedOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [speedOpen]);

  const v = () => video.current;
  const toggle = () => {
    const el = v();
    if (!el) return;
    if (el.paused) void el.play().catch(() => {});
    else el.pause();
  };
  const seekTo = (t: number) => {
    const el = v();
    if (el) el.currentTime = clampTime(t, el.duration);
  };
  const toggleFullscreen = () => {
    const el = root.current;
    if (!el) return;
    if (document.fullscreenElement === el)
      void document.exitFullscreen().catch(() => {});
    else void el.requestFullscreen?.().catch(() => {});
  };
  const apply = (action: VideoKeyAction) => {
    const el = v();
    if (!el) return;
    if (action.type === "toggle") toggle();
    else if (action.type === "seek") seekTo(el.currentTime + action.delta);
    else if (action.type === "fullscreen") toggleFullscreen();
    else if (action.type === "mute") el.muted = !el.muted;
    else el.playbackRate = stepSpeed(el.playbackRate, action.dir);
  };
  const wake = () => {
    setAwake(true);
    if (idle.current) clearTimeout(idle.current);
    idle.current = setTimeout(() => setAwake(false), VIDEO_IDLE_MS);
  };
  const shown = !playing || awake || speedOpen;

  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of several controls (role=group); a fieldset would be a form element inside the body
    <div
      ref={root}
      data-video-player=""
      contentEditable={false}
      role="group"
      aria-label={m.media_video_label({ name })}
      // Takes the focus when pressed (for keys) but stays out of the Tab order:
      // parts inside a document do not break the body's Tab path.
      tabIndex={-1}
      className={`relative w-full overflow-hidden bg-black outline-none ${fullscreen ? "h-full" : "rounded-[5px]"}`}
      style={fullscreen ? undefined : videoFrameStyle(width, ratio)}
      onMouseMove={coarse ? undefined : wake}
      onMouseLeave={() => setAwake(false)}
      // The focus sits on the root so keys (Space and so on) still reach the
      // player after pressing the bar. The bar's buttons and the seek bar stop
      // mousedown's default (taking the focus), so it is moved to the root here.
      // Only the volume range keeps the default (so it can be grabbed) and
      // hands the focus back to the root on release (below).
      onMouseDown={(e) => {
        if (coarse) return;
        if (e.target instanceof HTMLInputElement) return;
        if (document.activeElement !== e.currentTarget)
          e.currentTarget.focus({ preventScroll: true });
      }}
      onKeyDown={(e) => {
        if (coarse) return;
        if (e.key === "Escape" && speedOpen) {
          e.preventDefault();
          e.stopPropagation();
          setSpeedOpen(false);
          return;
        }
        // Space / Enter on a bar button is the button's own press.
        if (
          (e.key === " " || e.key === "Enter") &&
          e.target !== e.currentTarget
        )
          return;
        const action = videoKeyAction(e);
        if (!action) return;
        // Taken keys stop here: the app's shortcut listener on document does
        // not look at `defaultPrevented`, so preventing alone would let a
        // single-key shortcut run too.
        e.preventDefault();
        e.stopPropagation();
        apply(action);
        wake();
      }}
    >
      {/* biome-ignore lint/a11y/useMediaCaption: user-uploaded recordings have no caption source */}
      <video
        // A new element to load again (assigning the same src does not refetch).
        key={attempt}
        ref={video}
        // `#t=0.001` makes iOS Safari paint the first frame (black without it).
        src={`${src}#t=0.001`}
        preload="metadata"
        playsInline
        className="pointer-events-none absolute inset-0 h-full w-full object-contain"
        onLoadedMetadata={(e) => {
          const el = e.currentTarget;
          if (el.videoWidth > 0 && el.videoHeight > 0)
            setRatio(el.videoWidth / el.videoHeight);
          setDuration(el.duration);
        }}
        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onProgress={(e) => {
          const b = e.currentTarget.buffered;
          setBuffered(b.length > 0 ? b.end(b.length - 1) : 0);
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onVolumeChange={(e) => {
          setMuted(e.currentTarget.muted);
          setVolume(e.currentTarget.volume);
        }}
        onRateChange={(e) => setRate(e.currentTarget.playbackRate)}
        onError={() => {
          if (retry.current) clearTimeout(retry.current);
          if (attempt > 0) {
            setFailed(true);
            return;
          }
          retry.current = setTimeout(() => setAttempt(1), VIDEO_RETRY_MS);
        }}
      />
      {coarse ? (
        <button
          type="button"
          tabIndex={-1}
          data-video-open=""
          aria-label={m.media_video_play_named({ name })}
          className="absolute inset-0 flex items-center justify-center bg-transparent"
          // Pressing does not move the body's selection or focus (like the
          // image's open button). Without it the iOS tap moved the focus into
          // the editable area and the keyboard came up when opening the viewer.
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) =>
            openVideoViewer(e.currentTarget, {
              src,
              name,
              download,
              startAt: video.current?.currentTime ?? 0,
              onClose: (t) => {
                if (video.current) video.current.currentTime = t;
              },
            })
          }
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-white">
            <PlayIcon size={28} />
          </span>
          {duration > 0 && (
            <span className="absolute bottom-2 right-2 rounded-[4px] bg-black/65 px-1.5 py-0.5 text-[12px] tabular-nums text-white">
              {formatVideoTime(duration)}
            </span>
          )}
        </button>
      ) : (
        <>
          <button
            type="button"
            tabIndex={-1}
            data-video-surface=""
            aria-label={playing ? m.media_video_pause() : m.media_video_play()}
            className="absolute inset-0 cursor-pointer bg-transparent"
            onClick={() => {
              root.current?.focus({ preventScroll: true });
              toggle();
            }}
            onDoubleClick={toggleFullscreen}
          />
          {!playing && time === 0 && (
            <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white">
              <PlayIcon size={28} />
            </span>
          )}
          <div
            data-video-controls=""
            inert={!shown || undefined}
            className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-1.5 pt-6 text-white transition-opacity duration-200 ease-out motion-reduce:transition-none ${shown ? "opacity-100" : "pointer-events-none opacity-0"}`}
          >
            <SeekBar
              time={time}
              duration={duration}
              buffered={buffered}
              onSeek={seekTo}
            />
            <div className="flex items-center gap-1">
              <ControlButton
                label={playing ? m.media_video_pause() : m.media_video_play()}
                onClick={toggle}
              >
                {playing ? <PauseIcon /> : <PlayIcon />}
              </ControlButton>
              <ControlButton
                label={muted ? m.media_video_unmute() : m.media_video_mute()}
                onClick={() => {
                  const el = v();
                  if (el) el.muted = !el.muted;
                }}
              >
                {muted ? <MuteIcon /> : <VolumeIcon />}
              </ControlButton>
              <input
                type="range"
                tabIndex={-1}
                aria-label={m.media_video_volume()}
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                className="w-16 accent-white"
                onPointerUp={() => root.current?.focus({ preventScroll: true })}
                onChange={(e) => {
                  const el = v();
                  if (!el) return;
                  el.volume = Number(e.currentTarget.value);
                  el.muted = el.volume === 0;
                }}
              />
              <span className="ml-1 text-[12px] tabular-nums">
                {formatVideoTime(time)} / {formatVideoTime(duration)}
              </span>
              <span className="flex-1" />
              <div className="relative" data-video-speed="">
                <ControlButton
                  label={m.media_video_speed()}
                  hasPopup
                  expanded={speedOpen}
                  onClick={() => setSpeedOpen((o) => !o)}
                >
                  <span className="text-[12px] tabular-nums">{`${rate}x`}</span>
                </ControlButton>
                {speedOpen && (
                  <div
                    role="menu"
                    aria-label={m.media_video_speed()}
                    className="absolute bottom-full right-0 mb-2 flex min-w-24 flex-col rounded-[8px] bg-black/85 py-1"
                  >
                    {VIDEO_SPEEDS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        tabIndex={-1}
                        role="menuitemradio"
                        aria-checked={s === rate}
                        // The chosen speed is shown by a background, not bold.
                        className={`px-4 py-1 text-left text-[13px] hover:bg-white/15 ${s === rate ? "bg-white/15" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const el = v();
                          if (el) el.playbackRate = s;
                          setSpeedOpen(false);
                          root.current?.focus({ preventScroll: true });
                        }}
                      >
                        {s === 1 ? m.media_video_speed_normal() : `${s}x`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <ControlButton
                label={
                  fullscreen
                    ? m.media_video_exit_fullscreen()
                    : m.media_video_fullscreen()
                }
                onClick={toggleFullscreen}
              >
                {fullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
              </ControlButton>
            </div>
          </div>
        </>
      )}
      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black text-[13px] text-white">
          <span>{m.media_video_unplayable()}</span>
          <a href={download} tabIndex={-1} className="underline">
            {m.media_download()}
          </a>
        </div>
      )}
    </div>
  );
}

function ControlButton({
  label,
  onClick,
  children,
  hasPopup,
  expanded,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  hasPopup?: boolean;
  expanded?: boolean;
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      // Pressing does not take the focus (so Space does not press the button again).
      onMouseDown={(e) => e.preventDefault()}
      aria-haspopup={hasPopup ? "menu" : undefined}
      aria-expanded={hasPopup ? expanded : undefined}
      className="flex h-8 min-w-8 items-center justify-center rounded-[6px] px-1 text-white hover:bg-white/15"
      onClick={onClick}
    >
      {children}
    </button>
  );
}
